import { StepBackwardOutlined, StepForwardOutlined } from "@ant-design/icons";
import { Button, notification, Row, Space, Typography } from "antd";
import moment from "moment-timezone";
import React, { useEffect } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { DATE_DATA_FORMAT, DATE_FORMAT_2 } from "../../utils/date";

import { DroppableDay } from "./DroppableDay";
import { GRAY_400 } from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";
import { reorderList } from "../../utils/sort";
import ACTIONS from "../../store/action";
import { selectSite } from "../../store/selector";
import styled from "styled-components";

const StyledDiv = styled.div`
  height: ${(props) => (props.isSavedListView ? "calc(100vh - 380px)" : "calc(100vh - 355px)")};
  width: ${(props) => (props.filtersVisible ? "calc(100vw - 340px)" : "100%")};
  overflow: auto;
  margin-left: ${(props) => (props.filtersVisible ? "30px" : "10px")};
  ::-webkit-scrollbar {
    width: 15px;
    -webkit-appearance: none;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 4px solid white;
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const { Title } = Typography;

const OrdersMonthlyCalendarHeader = ({
  startDayOfMonthlyCalendar,
  lastDayOfMonthlyCalendar,
  onClickToday,
  isFetching,
  onClickMonthBackward,
  onClickMonthForward,
  filtersVisible
}) => {
  const { t } = useTranslation();
  return (
    <Row
      justify="space-between"
      gutter={[0, 16]}
      style={{ padding: filtersVisible ? "20px 40px 20px 30px" : "20px 40px 20px 35px", overflow: "hidden" }}
    >
      <Title level={4}>{`${startDayOfMonthlyCalendar.format("MMM D")} - ${lastDayOfMonthlyCalendar.format(
        DATE_FORMAT_2
      )}`}</Title>
      <Space id="cv-time-bar">
        <Button
          disabled={isFetching}
          ghost={true}
          onClick={onClickMonthBackward}
          icon={<StepBackwardOutlined style={{ fontSize: 20 }} />}
        />
        <Button disabled={isFetching} ghost={true} onClick={onClickToday}>
          {t("today")}
        </Button>
        <Button
          disabled={isFetching}
          ghost={true}
          onClick={onClickMonthForward}
          icon={<StepForwardOutlined style={{ fontSize: 20 }} />}
        />
      </Space>
    </Row>
  );
};

const MonthlyCalendarView = ({
  patients,
  isFetching,
  startDayOfMonthlyCalendar,
  lastDayOfMonthlyCalendar,
  onClickCalendar,
  monthOrdersGroupByDays,
  setMonthOrdersGroupByDays,
  firstDayOfMonth,
  setFirstDayOfMonth,
  filtersVisible,
  isSavedListView
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const site = useSelector(selectSite);

  useEffect(() => {
    const numberOfDays = lastDayOfMonthlyCalendar.diff(startDayOfMonthlyCalendar, "days") + 1;

    const monthlyCalendarRange = moment.range(startDayOfMonthlyCalendar, lastDayOfMonthlyCalendar);
    const monthOrders = patients.filter((p) => monthlyCalendarRange.contains(moment(p.currentOrder.orderDate)));
    const monthOrdersGroupByDays = Array.from({ length: numberOfDays }, (v, k) => k).map((k) =>
      monthOrders.filter((o) => moment(o.currentOrder.orderDate).diff(startDayOfMonthlyCalendar, "days") === k)
    );
    setMonthOrdersGroupByDays(monthOrdersGroupByDays);
  }, [patients, startDayOfMonthlyCalendar, lastDayOfMonthlyCalendar]);

  const onClickMonthToday = () => {
    setFirstDayOfMonth(moment().startOf("month"));
  };

  const onClickMonthBackward = () => {
    setFirstDayOfMonth(moment(firstDayOfMonth).subtract(1, "months"));
  };

  const onClickMonthForward = () => {
    setFirstDayOfMonth(moment(firstDayOfMonth).add(1, "months"));
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      if (source.index === destination.index) return;
      const items = reorderList(monthOrdersGroupByDays[source.droppableId], source.index, destination.index);
      const newMonthOrdersGroupByDays = Array.from(monthOrdersGroupByDays);
      newMonthOrdersGroupByDays[source.droppableId] = items;
      setMonthOrdersGroupByDays(newMonthOrdersGroupByDays);
    } else {
      const patient = monthOrdersGroupByDays[source.droppableId][source.index];
      const sourceDate = moment(firstDayOfMonth).add(source.droppableId, "days");
      const destinationDate = moment(firstDayOfMonth).add(destination.droppableId, "days");

      const diff = destinationDate.diff(sourceDate, "days");
      const orderDate = moment(patient.currentOrder.orderDate);
      const newOrderDate = orderDate.add(diff, "days");
      const newPatient = {
        id: patient.id,
        pharmacyPatient: patient.id,
        orderDate: newOrderDate.toISOString()
      };
      const newCurrentOrder = {
        id: patient.currentOrder.id,
        orderDate: newOrderDate.toISOString()
      };

      notification.open({
        message: t("orders_calender.order_table_changed"),
        description: t("orders_calender.order_date_changed", {
          formDate: moment(patient.currentOrder.orderDate).format(DATE_DATA_FORMAT),
          toDate: newOrderDate.format(DATE_DATA_FORMAT)
        })
      });

      const newMonthOrdersGroupByDays = Array.from(monthOrdersGroupByDays);
      newMonthOrdersGroupByDays[destination.droppableId][destination.index] = {
        ...patient,
        currentOrder: { ...patient.currentOrder, orderDate: newPatient.orderDate }
      };
      newMonthOrdersGroupByDays[source.droppableId].splice(source.index, 1);
      setMonthOrdersGroupByDays(newMonthOrdersGroupByDays);

      dispatch(ACTIONS.bulkChangeAction(newPatient, [newCurrentOrder]));
    }
  };

  return (
    <>
      <OrdersMonthlyCalendarHeader
        startDayOfMonthlyCalendar={startDayOfMonthlyCalendar}
        lastDayOfMonthlyCalendar={lastDayOfMonthlyCalendar}
        onClickToday={onClickMonthToday}
        onClickMonthBackward={onClickMonthBackward}
        onClickMonthForward={onClickMonthForward}
        isFetching={isFetching}
        filtersVisible={filtersVisible}
      />
      <StyledDiv filtersVisible={filtersVisible} isSavedListView={isSavedListView}>
        <DragDropContext onDragEnd={onDragEnd}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gridGap: "0.6rem",
              marginBottom: "20px"
            }}
          >
            {monthOrdersGroupByDays.map((dailyOrders, dayIndex) => {
              const date = moment(startDayOfMonthlyCalendar).add(dayIndex, "days");
              const isSameMonth = moment(firstDayOfMonth).isSame(date, "month");
              const isFirstDayOfMonth = moment(date).date() === 1;

              return (
                <Space key={dayIndex} direction="vertical" style={{ alignItems: "center" }}>
                  {dayIndex <= 6 && (
                    <Title
                      level={5}
                      style={{
                        textAlign: "center",
                        color: GRAY_400,
                        fontWeight: fontWeights.semibold,
                        fontSize: sizes.medium,
                        textTransform: "uppercase"
                      }}
                    >
                      {date.format("ddd")}
                    </Title>
                  )}
                  <Title level={5} style={{ textAlign: "center", opacity: isSameMonth ? 1 : 0.4 }}>
                    {isFirstDayOfMonth ? date.format("D MMM") : date.format("D")}
                  </Title>
                  <DroppableDay
                    key={`${dayIndex}-${dailyOrders.length}`}
                    dailyOrders={dailyOrders}
                    dayIndex={dayIndex}
                    onClickCalendar={onClickCalendar}
                    mark={date.format(DATE_DATA_FORMAT) === moment().format(DATE_DATA_FORMAT)}
                    site={site}
                  />
                </Space>
              );
            })}
          </div>
        </DragDropContext>
      </StyledDiv>
    </>
  );
};

export default MonthlyCalendarView;
